// =======================
// TrialBalanceGroupPDF.js
// =======================
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

// =======================
// STYLES
// =======================
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    flexDirection: "column",
    padding: 0,
  },
  table: {
    display: "table",
    width: "auto",
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
  },
  firstCol: { borderLeftWidth: 1 },
  topBorder: { borderTopWidth: 1 },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 4,
    flexWrap: "wrap",
    wordBreak: "break-word",
  },
  tableColHeader: {
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
    textAlign: "center",
  },
  nodeRow: {
    backgroundColor: "#cce5ff",
    fontWeight: "bold",
    padding: 4,
  },
  subtotalRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    padding: 4,
  },
  totalRow: {
    backgroundColor: "#b3b3b3",
    fontWeight: "bold",
    padding: 4,
  },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  headerImage: { padding: 2 },
});

// =======================
// HEADER COMPONENT
// =======================
let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : "-";

const Header = ({ parentDetails }) => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ padding: 10 }}>
      <View style={{ width: "100%", flexDirection: "column" }}>
        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
          <Image
            src={apiPaths_from_local_storage[0].REP_HEADER}
            style={styles.headerImage}
          />
        )}
        <View style={{ textAlign: "center", marginTop: 10 }}>
          <Text style={{ fontSize: 12, fontWeight: "bold", marginTop: 5 }}>
            Trial Balance Group Report
          </Text>
        </View>
      </View>
    </View>
  </View>
);

// =======================
// FOOTER COMPONENT
// =======================
const Footer = () => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ width: "100%", padding: 10 }}>
      <View style={{ width: "100%", fontSize: 10 }}>
        <Text
          style={{ textAlign: "center" }}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </View>
    </View>
  </View>
);

// =======================
// TABLE CONTENT COMPONENT
// =======================
const TableContent = ({ groupedData, columns, colWidths, grandTotal }) => (
  <View style={{ flexGrow: 1 }} wrap>
    <View style={{ padding: 10 }}>
      {/* Table Header */}
      <View style={styles.tableRow} fixed>
        {columns.map((col, i) => (
          <View
            key={i}
            style={[
              styles.tableCol,
              styles.tableColHeader,
              styles.topBorder,
              i === 0 && styles.firstCol,
              { flex: colWidths[i] },
            ]}
          >
            <Text>{col.column_name}</Text>
          </View>
        ))}
      </View>

      {/* Table Body */}
      {Object.entries(groupedData).map(([node, rows], nodeIndex) => {
        // Calculate subtotal from explicit net row balances
        const nodeSubtotal = rows.reduce(
          (acc, r) => {
            const bal = r.BALANCE || 0;
            if (bal > 0) acc.DEBIT += bal;
            else if (bal < 0) acc.CREDIT += Math.abs(bal);
            return acc;
          },
          { DEBIT: 0, CREDIT: 0 }
        );

        return (
          <React.Fragment key={nodeIndex}>
            {/* NODE Header */}
            <View style={styles.tableRow} wrap={false}>
              <View
                style={[
                  styles.tableCol,
                  styles.nodeRow,
                  styles.firstCol,
                  { flex: colWidths.reduce((a, b) => a + b) },
                ]}
              >
                <Text>{node}</Text>
              </View>
            </View>

            {/* NODE Rows */}
            {rows.map((row, i) => (
              <View style={styles.tableRow} key={i} wrap={false}>
                {columns.map((col, ci) => {
                  const key = col.column_key_from_api;
                  const balance = row["BALANCE"] || 0;
                  let displayValue = row[key] ?? "";

                  // Net Balance row splitting values
                  if (key === "DEBIT") {
                    displayValue = balance > 0 ? balance.toFixed(2) : "";
                  } else if (key === "CREDIT") {
                    displayValue = balance < 0 ? Math.abs(balance).toFixed(2) : "";
                  }

                  return (
                    <View
                      key={ci}
                      style={[
                        styles.tableCol,
                        ci === 0 && styles.firstCol,
                        { flex: colWidths[ci] },
                      ]}
                    >
                      <Text
                        style={
                          ["DEBIT", "CREDIT"].includes(key)
                            ? styles.textRight
                            : styles.textLeft
                        }
                      >
                        {displayValue}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}

            {/* NODE Subtotal */}
            <View style={styles.tableRow} wrap={false}>
              {columns.map((col, i) => {
                let content = "";
                if (col.column_key_from_api === "DEBIT")
                  content = nodeSubtotal.DEBIT > 0 ? nodeSubtotal.DEBIT.toFixed(2) : "";
                else if (col.column_key_from_api === "CREDIT")
                  content = nodeSubtotal.CREDIT > 0 ? nodeSubtotal.CREDIT.toFixed(2) : "";
                else if (i === 0) content = "Node Total:";

                return (
                  <View
                    key={i}
                    style={[
                      styles.tableCol,
                      styles.subtotalRow,
                      i === 0 && styles.firstCol,
                      { flex: colWidths[i] },
                    ]}
                  >
                    <Text
                      style={
                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                          ? styles.textRight
                          : styles.textLeft
                      }
                    >
                      {content}
                    </Text>
                  </View>
                );
              })}
            </View>
          </React.Fragment>
        );
      })}

      {/* GRAND TOTAL */}
      <View style={styles.tableRow} wrap={false}>
        {columns.map((col, i) => {
          let content = "";
          if (col.column_key_from_api === "DEBIT")
            content = grandTotal.DEBIT > 0 ? grandTotal.DEBIT.toFixed(2) : "";
          else if (col.column_key_from_api === "CREDIT")
            content = grandTotal.CREDIT > 0 ? grandTotal.CREDIT.toFixed(2) : "";
          else if (i === 0) content = "Grand Total:";

          return (
            <View
              key={i}
              style={[
                styles.tableCol,
                styles.totalRow,
                i === 0 && styles.firstCol,
                { flex: colWidths[i] },
              ]}
            >
              <Text
                style={
                  ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                    ? styles.textRight
                    : styles.textLeft
                }
              >
                {content}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  </View>
);

// =======================
// MAIN PDF COMPONENT
// =======================
const TrialBalanceGroupPDF = ({
  data,
  parentDetails,
  selected_mode_table_structure,
}) => {
  const reportData = data.filter((r) => r.DEBIT || r.CREDIT || r.BALANCE);

  const selectedMode = parentDetails.mode_list_array.find((m) => m.is_selected);
  
  // Stripped out NODE and BALANCE columns so they don't break grid sizing
  const columns = selected_mode_table_structure
    .find((item) => item.mode_table_code === selectedMode?.mode_code)
    ?.mode_table_columns.filter(
      (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "BALANCE"
    ) || [];

  const colWidths = columns.map((col) =>
    ["DEBIT", "CREDIT"].includes(col.column_key_from_api) ? 1 : 2
  );

  const groupedData = reportData.reduce((acc, row) => {
    if (!acc[row.NODE]) acc[row.NODE] = [];
    acc[row.NODE].push(row);
    return acc;
  }, {});

  // Calculate pure grand total based on explicit net row balances
  const grandTotal = reportData.reduce(
    (acc, r) => {
      const bal = r.BALANCE || 0;
      if (bal > 0) acc.DEBIT += bal;
      else if (bal < 0) acc.CREDIT += Math.abs(bal);
      return acc;
    },
    { DEBIT: 0, CREDIT: 0 }
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header parentDetails={parentDetails} />
        <TableContent
          groupedData={groupedData}
          columns={columns}
          colWidths={colWidths}
          grandTotal={grandTotal}
        />
        <Footer />
      </Page>
    </Document>
  );
};

export default TrialBalanceGroupPDF;